import { io, type Socket } from "socket.io-client";
import {
  LIVE_VIEW_NAMESPACE,
  LiveViewEvents,
  type PresenceEntry
} from "shared/utils/live-view/protocol";
import { errorSummary } from "./errorSummary";
import {
  isPresenceEntry,
  presenceFromSnapshot,
  pruneStalePresence,
  removePresence,
  upsertPresence,
  type PresenceMap
} from "./presence";

const WS_URL = import.meta.env.VITE_APP_INBOX_WS_URL;

const HANDSHAKE_RETRY_BASE_MS = 2_000;
const HANDSHAKE_RETRY_MAX_MS = 30_000;

type GetFreshToken = (options?: { force?: boolean }) => Promise<string | null>;

/**
 * Opens the `/live-view` socket with a fresh ticket on every (re)connect.
 *
 * socket.io retries transport failures itself, but not a handshake the server
 * refused (e.g. an expired ticket) nor a disconnect the server initiated (it
 * drops sockets once their ticket expires). Both reconnect here with a newly
 * minted ticket, backing off; a server disconnect is expected every ticket
 * lifetime, so its first retry is immediate. `onConnect` fires on every
 * (re)connect, which is where callers re-join.
 */
export const openLiveViewSocket = ({
  getFreshToken,
  onConnect,
  onDisconnect
}: {
  getFreshToken: GetFreshToken;
  onConnect: () => void;
  /** Disconnected, or a connection attempt failed. */
  onDisconnect: (event: { reason: string; dropped: boolean }) => void;
}): { socket: Socket; close: () => void } => {
  let forceFreshTicket = false;
  let retryAttempt = 0;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;

  const socket = io(`${WS_URL}${LIVE_VIEW_NAMESPACE}`, {
    transports: ["websocket"],
    auth: (cb) => {
      const force = forceFreshTicket;
      forceFreshTicket = false;
      getFreshToken({ force })
        .then((token) => cb(token ? { token } : {}))
        .catch((error: unknown) => {
          // Never log the error itself: its config carries the Bearer token.
          console.warn("[LiveView] Ticket request failed", errorSummary(error));
          cb({});
        });
    },
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 30000,
    reconnectionAttempts: Infinity
  });

  const reconnectWithFreshTicket = ({ immediate = false } = {}) => {
    forceFreshTicket = true;
    const delay =
      immediate && retryAttempt === 0
        ? 0
        : Math.min(
            HANDSHAKE_RETRY_BASE_MS * 2 ** retryAttempt,
            HANDSHAKE_RETRY_MAX_MS
          );
    retryAttempt += 1;
    clearTimeout(retryTimer);
    retryTimer = setTimeout(() => socket.connect(), delay);
  };

  socket.on("connect", () => {
    retryAttempt = 0;
    onConnect();
  });

  socket.on("disconnect", (reason) => {
    console.warn(`[LiveView] Disconnected: ${reason}`);
    onDisconnect({ reason, dropped: true });
    if (reason === "io server disconnect")
      reconnectWithFreshTicket({ immediate: true });
  });

  socket.on("connect_error", (error) => {
    console.warn(`[LiveView] Connection error: ${error.message}`);
    onDisconnect({ reason: "connect_error", dropped: false });
    if (!socket.active) reconnectWithFreshTicket();
  });

  return {
    socket,
    close: () => {
      clearTimeout(retryTimer);
      socket.removeAllListeners();
      socket.disconnect();
    }
  };
};

const PRUNE_INTERVAL_MS = 15_000;

/**
 * Keeps a presence map from `presence:snapshot` / `presence` /
 * `presence:left`, dropping entries that stop being refreshed. `accepts`
 * filters entries (e.g. to the applications a list page asked for). Returns
 * a cleanup for the prune timer; the listeners go with the socket.
 */
export const subscribePresence = (
  socket: Socket,
  {
    read,
    write,
    accepts = () => true
  }: {
    read: () => PresenceMap;
    write: (next: PresenceMap) => void;
    accepts?: (entry: PresenceEntry) => boolean;
  }
) => {
  socket.on(LiveViewEvents.PresenceSnapshot, (snapshot: unknown) => {
    const applicants =
      snapshot && typeof snapshot === "object"
        ? (snapshot as { applicants?: unknown }).applicants
        : undefined;
    const entries = Array.isArray(applicants) ? applicants : [];
    write(
      presenceFromSnapshot(
        entries.filter((entry) => isPresenceEntry(entry) && accepts(entry)),
        Date.now()
      )
    );
  });

  socket.on(LiveViewEvents.Presence, (entry: unknown) => {
    if (!isPresenceEntry(entry) || !accepts(entry)) return;
    write(upsertPresence(read(), entry, Date.now()));
  });

  socket.on(LiveViewEvents.PresenceLeft, (left: unknown) => {
    const applicantId =
      left && typeof left === "object"
        ? (left as { applicant_id?: unknown }).applicant_id
        : undefined;
    if (typeof applicantId !== "number") return;
    write(removePresence(read(), applicantId));
  });

  const pruneTimer = setInterval(() => {
    write(pruneStalePresence(read(), Date.now()));
  }, PRUNE_INTERVAL_MS);

  return () => clearInterval(pruneTimer);
};
